import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, ShieldOff } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { ME_KEY, useMe } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/field';
import { Alert, Card, CardHeader, PageHeader } from '@/components/ui/misc';
import { useToast } from '@/components/ui/toast';

export function Account() {
  const { data: me } = useMe();
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!me) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (next !== confirm) return setError('The new passwords do not match.');
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/auth/password', { currentPassword: current, newPassword: next });
      setCurrent('');
      setNext('');
      setConfirm('');
      toast.ok('Password changed. Other sessions were signed out.');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-6">
      <PageHeader title="Account and security" description={`${me.user.name} · ${me.user.email}`} />
      <div className="flex flex-col gap-5">
        <Card>
          <CardHeader title="Two-factor authentication" />
          <div className="flex items-center gap-3 px-5 py-4 text-sm">
            {me.user.mfaEnabled ? (
              <>
                <ShieldCheck className="size-5 text-ok" />
                <span>On. You enter a code from your authenticator app when you sign in.</span>
              </>
            ) : (
              <>
                <ShieldOff className="size-5 text-warn" />
                <span className="flex-1">Off. Add a second factor to protect program material.</span>
                <Link to="/mfa/setup">
                  <Button size="sm">Set up</Button>
                </Link>
              </>
            )}
          </div>
        </Card>
        {me.user.mfaEnabled && <RecoveryCodes left={me.user.recoveryCodesLeft} email={me.user.email} />}
        <Card>
          <CardHeader title="Change password" description="Changing it signs you out everywhere else." />
          <form onSubmit={submit} className="flex flex-col gap-4 px-5 py-4">
            {error && <Alert tone="bad">{error}</Alert>}
            <Field label="Current password" htmlFor="cur">
              <Input
                id="cur"
                type="password"
                autoComplete="current-password"
                required
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="New password" htmlFor="new" hint="At least 12 characters.">
                <Input
                  id="new"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={12}
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                />
              </Field>
              <Field label="Confirm new password" htmlFor="conf">
                <Input
                  id="conf"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </Field>
            </div>
            <div>
              <Button type="submit" loading={busy}>
                Change password
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}

/** Replace every recovery code; the old ones stop working immediately. */
function RecoveryCodes({ left, email }: { left: number; email: string }) {
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();

  async function regenerate(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ recoveryCodes: string[] }>('/api/auth/mfa/recovery-codes', { code: code.trim() });
      setCodes(res.recoveryCodes);
      setCode('');
      void qc.invalidateQueries({ queryKey: ME_KEY });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const text = codes ? `HELM recovery codes for ${email}\nEach code works once.\n\n${codes.join('\n')}\n` : '';
  return (
    <Card>
      <CardHeader
        title="Recovery codes"
        description={`${left} of 10 left. Each signs you in once if you lose your phone. Generating new codes cancels all the old ones.`}
      />
      <div className="flex flex-col gap-3 px-5 py-4">
        {codes ? (
          <>
            <Alert tone="warn" title="Save these now; they will not be shown again.">
              Keep them in a password manager, not in a synced or project folder.
            </Alert>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-md border border-border bg-surface-2 px-5 py-4 font-mono text-sm tabular">
              {codes.map((c) => (
                <span key={c}>{c}</span>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => navigator.clipboard.writeText(text)}>
                Copy
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setCodes(null)}>
                Done
              </Button>
            </div>
          </>
        ) : (
          <form onSubmit={regenerate} className="flex flex-wrap items-end gap-3">
            {error && (
              <Alert tone="bad" className="w-full">
                {error}
              </Alert>
            )}
            <Field label="Authenticator code" htmlFor="rc-code" hint="A recovery code cannot be used here.">
              <Input
                id="rc-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                className="w-36 tabular tracking-[0.2em]"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </Field>
            <Button type="submit" variant="secondary" loading={busy} disabled={code.trim().length < 6}>
              Generate new codes
            </Button>
          </form>
        )}
      </div>
    </Card>
  );
}
